import React, { useCallback, useContext, useMemo } from 'react'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import Group from 'components/utils/Group'
import FilterChip from 'LuxKit/Chips/FilterChip'
import { getCurrentDateLabel, getCurrentOccupancyLabel } from '../../FlashHotel/FlashHotelMobileVariant/FlashHotelMobileVariantUtils'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import LineUsersAltIcon from 'LuxKit/Icons/line/LineUsersAltIcon'
import ModalContext from 'contexts/ModalContext'
import ModalBase from 'LuxKit/Modal/ModalBase'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import ModalBody from 'LuxKit/Modal/ModalBody'
import OfferCapacityWithAgesSelection from '../../Filters/CapacityWithAges/OfferCapacityWithAgesSelection'
import ModalFooter from 'LuxKit/Modal/ModalFooter'
import OfferSidebarDatePicker from 'components/OfferPage/Filters/SidebarDatePicker/OfferSidebarDatePicker'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { BusinessTravellerMultiRoomHotelsOfferPageMobile, BusinessTravellerMultiRoomHotelsOfferPageMobileDisabled } from 'businessTraveller/components/select-traveller/multi-room-hotels'
import { isLebtMultiRoomHotelEnabled } from 'businessTraveller/selectors/multiRoomHotelSelectors'
import BusinessTravellerAccountGuard from 'businessTraveller/components/BusinessTravellerAccountGuard'
import BusinessTravellerSelectLoadingSkeleton from 'businessTraveller/components/select-traveller/BusinessTravellerSelectLoadingSkeleton'

interface Props {
  offer: App.HotelOffer;
  dateLabel: string;
  occupancyLabel: string;
  isLebtMultiRoomHotelEnabled: boolean;
}

function AlwaysOnMobileStickyHeaderFilters(props: Props) {
  const {
    offer,
    dateLabel,
    occupancyLabel,
    isLebtMultiRoomHotelEnabled,
  } = props

  const dispatch = useOfferPageDispatch()
  const showModal = useContext(ModalContext)

  const capacities = useMemo(() => {
    return offer.packages.flatMap((p) => p.roomRate.capacities)
  }, [offer.packages])

  const onClose = useCallback(() => {
    dispatch({ type: OfferPageActions.closeHeaderFilter })
  }, [dispatch])

  const onOpenCalendar = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'checkin' })

    showModal((resolve) => <ModalBase mode="drawer">
      <form onSubmit={e => {
        e.preventDefault()
        resolve()
      }}
      >
        <OfferSidebarDatePicker />
      </form>
    </ModalBase>).then(onClose)
  }, [dispatch, showModal, onClose])

  const onOpenCapacity = useCallback(() => {
    dispatch({ type: OfferPageActions.toggleHeaderFilter, filter: 'capacity' })

    showModal(<ModalBase mode="drawer">
      <ModalHeader title="Who is going?" />
      <ModalBody>
        <OfferCapacityWithAgesSelection capacities={capacities} />
      </ModalBody>
      <ModalFooter
        primaryActionProps={{
          children: 'Apply',
        }}
      />
    </ModalBase>).then(onClose)
  }, [dispatch, showModal, capacities, onClose])

  return (
    <Group direction="horizontal" gap={8} wrap="wrap">
      {isLebtMultiRoomHotelEnabled && <BusinessTravellerAccountGuard
        employeeRoles={['BUSINESS_ADMIN', 'BUSINESS_MANAGER']}
        loading={<BusinessTravellerSelectLoadingSkeleton />}
        fallback={<BusinessTravellerMultiRoomHotelsOfferPageMobileDisabled />}
      >
        <BusinessTravellerMultiRoomHotelsOfferPageMobile />
      </BusinessTravellerAccountGuard>}
      <FilterChip
        startIcon={<LineCalendarIcon />}
        size="medium"
        onClick={onOpenCalendar}
      >
        {dateLabel || 'Anytime'}
      </FilterChip>
      {!isLebtMultiRoomHotelEnabled && occupancyLabel && <FilterChip
        startIcon={<LineUsersAltIcon />}
        size="medium"
        onClick={onOpenCapacity}
      >
        {occupancyLabel}
      </FilterChip>}
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  offer: state.offer,
  dateLabel: getCurrentDateLabel(state),
  occupancyLabel: getCurrentOccupancyLabel(state),
  isLebtMultiRoomHotelEnabled: isLebtMultiRoomHotelEnabled(appState),
}), AlwaysOnMobileStickyHeaderFilters)
